import React from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AlertsPage = ({ alerts, onRefresh, toast }) => {
  const markComplete = async (alertId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/alerts/${alertId}/complete`);
      toast({ title: 'Marcado como realizado', description: 'Los agentes han sido notificados.' });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const getSeverityColor = (severity, type) => {
    if (type === 'daily_report') return 'border-teal-500 bg-teal-50';
    if (severity === 'critical') return 'border-red-500 bg-red-50';
    if (severity === 'warning') return 'border-amber-500 bg-amber-50';
    return 'border-blue-500 bg-blue-50';
  };

  const getSeverityIcon = (severity, type) => {
    if (type === 'daily_report') return <span className="text-teal-600">📊</span>;
    if (severity === 'critical') return <XCircle className="h-4 w-4 text-red-600" />;
    if (severity === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    return <span className="text-blue-600">ℹ️</span>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Alertas y notificaciones
            </CardTitle>
            <CardDescription>
              Solo el Coordinador te habla aquí. Una alerta por cosa. Sin spam.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <p className="text-slate-500 text-center py-12">Sin alertas. Todo bajo control.</p>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert, i) => {
              const isReport = alert.alert_type === 'daily_report';

              if (isReport) {
                return (
                  <details key={i} className={`p-4 rounded-lg border-l-4 ${getSeverityColor(alert.severity, alert.alert_type)}`}>
                    <summary className="cursor-pointer flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getSeverityIcon(alert.severity, alert.alert_type)}
                        <span className="font-semibold text-teal-900">{alert.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">
                          {new Date(alert.timestamp).toLocaleString('es-ES')}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            const blob = new Blob([alert.message || ''], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `informe-${alert.date || 'hoy'}.txt`;
                            link.click();
                          }}
                        >
                          Descargar
                        </Button>
                      </div>
                    </summary>
                    <div className="mt-4 pt-3 border-t border-teal-200">
                      <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-slate-800 max-h-[600px] overflow-auto">
                        {alert.message}
                      </pre>
                    </div>
                  </details>
                );
              }

              return (
                <div key={i} className={`p-4 rounded-lg border-l-4 ${getSeverityColor(alert.severity, alert.alert_type)}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getSeverityIcon(alert.severity, alert.alert_type)}
                        <span className="font-semibold">{alert.title}</span>
                        {alert.completed && <Badge variant="outline" className="text-xs">Realizado</Badge>}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{alert.message}</p>

                      {alert.why_important && (
                        <p className="text-xs text-slate-500 mt-2 italic">Por qué importa: {alert.why_important}</p>
                      )}
                      {alert.what_system_does && (
                        <p className="text-xs text-green-600 mt-1">El sistema hace: {alert.what_system_does}</p>
                      )}
                      {alert.action_required && (
                        <div className="mt-2 p-2 bg-white rounded border border-slate-200">
                          <p className="text-xs font-semibold text-slate-700">Tú tienes que hacer:</p>
                          <p className="text-sm text-slate-800 mt-1">{alert.action_required}</p>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(alert.timestamp).toLocaleString('es-ES')}
                      </p>
                    </div>
                    {!alert.completed && alert.action_required && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 shrink-0"
                        onClick={() => markComplete(alert.id)}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Ya realizado
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsPage;
